import {
  ColumnConfiguration,
  RowConfiguration,
} from '@epicuro-next/ataraxia/grid-builder';
import { SizeGridRowConfig } from '@epicuro-next/data/order-book';
import {
  OrderSize,
  Size,
  SortedScaleSetMap,
} from '@epicuro-next/models/order-book';
import {
  getScaleSetMap,
  getTitleColumns,
  orderDetailSizeGridRowsLocale,
} from '@epicuro-next/state/order-book';
import { getValue, pick } from '@epicuro-next/utilities';

import { IOrderRowProduct } from '../../models/order-product.model';

type SizeQuantityProps =
  | 'requestedQuantity'
  | 'confirmedQuantity'
  | 'unconfirmedQuantity'
  | 'deliveredQuantity'
  | 'quantity';

const defaultSizeQuantityProperties: SizeQuantityProps[] = [
  'quantity',
  'confirmedQuantity',
  'deliveredQuantity',
  'requestedQuantity',
  'unconfirmedQuantity',
];

export function getRows<
  T extends {
    productId: string;
    title: string;
    sizes: (OrderSize | Size)[];
  },
>(
  materials: T[],
  products: IOrderRowProduct[],
  showStyleInHeader = true,
  selectedScaleSet?: string,
  // used for getting properties outside size (price, etc.)
  additionalOrderItemData: Record<string, unknown> = {},
  // used for custom rows for different properties (PUMA)
  customRowsConfig: SizeGridRowConfig[] = [],
  rowTitleProperty: keyof T = 'title',
): RowConfiguration<unknown>[] {
  if (products.length === 0) return [];

  const rows = materials.flatMap((material) => {
    const product = products.find(
      ({ id }) => id === material.productId,
    ) as IOrderRowProduct;
    const additionalData = {
      title: material.title,
      image: product?.thumbnail,
    };
    const widthScaleSetMap = getScaleSetMap(product.sortedScaleSets, 'width');

    if (product.variantAux && product.variantAux.length > 1) {
      const widthKey = product.sortedScaleSets[1]?.key;
      const childRows = product.variantAux.map((aux) => {
        const filteredSizes = material.sizes.filter(
          (size) => size.sizeAttributes[widthKey] === aux.masterValue,
        );

        return {
          rowTitle: getRowTitle(product, aux.masterValue, widthScaleSetMap),
          data: {
            ...getEmptySizeRowData(product, defaultSizeQuantityProperties),
            ...getSizesRowData(
              filteredSizes,
              defaultSizeQuantityProperties,
              additionalOrderItemData,
            ),
          },
        };
      });

      return showStyleInHeader
        ? {
            additionalData,
            rowTitle: '',
            data: {},
            childRows,
          }
        : childRows;
    }

    if (customRowsConfig.length > 0) {
      return customRowsConfig.map((configItem) => {
        return {
          rowTitle:
            orderDetailSizeGridRowsLocale[configItem.rowId] ?? configItem.rowId,
          data: {
            ...getEmptySizeRowData(product, defaultSizeQuantityProperties),
            ...getSizesValuesData(
              material.sizes,
              configItem.pathToSizeProperty,
              additionalOrderItemData,
            ),
          },
        };
      });
    }

    const row = {
      additionalData,
      rowTitle: product.variantAux
        ? getRowTitle(
            product,
            product.variantAux[0].masterValue,
            widthScaleSetMap,
            selectedScaleSet,
          )
        : ((material[rowTitleProperty] ?? material.title) as string),
      data: {
        ...getEmptySizeRowData(product, defaultSizeQuantityProperties),
        ...getSizesRowData(material.sizes, defaultSizeQuantityProperties),
      },
    };
    return showStyleInHeader ? row : [row];
  });

  return showStyleInHeader ? rows : rows.flat(1);
}

export function getSizesRowData<T extends OrderSize | Size>(
  sizes: T[],
  keys: SizeQuantityProps[],
  additionalData: Record<string, unknown> = {},
) {
  return sizes.reduce(
    (
      acc: Record<string, { [value in SizeQuantityProps]: number | string }>,
      size,
    ) => {
      acc[size.sizeAttributes?.size ?? size.title] = pick(
        {
          ...size,
          ...additionalData,
        },
        keys,
      ) as {
        [value in SizeQuantityProps]: number;
      };
      return acc;
    },
    {},
  );
}

// used for custom rows for different properties (PUMA)
// pass all to quantity property
export function getSizesValuesData<T extends OrderSize | Size>(
  sizes: T[],
  path: string,
  additionalData: Record<string, unknown> = {},
) {
  return sizes.reduce(
    (acc: Record<string, { quantity: number | string }>, size) => {
      acc[size.sizeAttributes?.size ?? size.title] = {
        quantity:
          getValue(
            {
              ...size,
              ...additionalData,
            },
            path,
          ) ?? '-',
      };
      return acc;
    },
    {},
  );
}

function getEmptySizeRowData(
  product: IOrderRowProduct,
  sizeQuantityProperties: SizeQuantityProps[],
) {
  return product.variantSizes.reduce(
    (acc: Record<string, { [value in SizeQuantityProps]?: string }>, size) => {
      acc[size.masterValue] = sizeQuantityProperties.reduce(
        (res: { [value in SizeQuantityProps]?: string }, prop) => {
          res[prop] = '-';
          return res;
        },
        {},
      );

      return acc;
    },
    {},
  );
}

export function getColumns(
  product: IOrderRowProduct,
  selectedScaleSet?: string,
): ColumnConfiguration[] {
  if (!product) return [];

  const sizeScaleSetMap = getScaleSetMap(product.sortedScaleSets, 'size');

  return [...product.variantSizes]
    .sort((a, b) => {
      return (
        (sizeScaleSetMap[a.value]?.sortValue ?? 0) -
        (sizeScaleSetMap[b.value]?.sortValue ?? 0)
      );
    })
    .map((size) => ({
      columnName: getTitleColumns(
        [size.masterValue ?? size.value],
        sizeScaleSetMap,
        selectedScaleSet,
      )[0].value,
      bindProperty: size.masterValue ?? size.value,
    }));
}

export function getRowTitle(
  product: IOrderRowProduct,
  auxMasterValue: string,
  sortedScaleSetsMap: SortedScaleSetMap,
  selectedScaleSet?: string,
): string {
  let auxId = '';
  const auxValue = getTitleColumns(
    [auxMasterValue],
    sortedScaleSetsMap,
    selectedScaleSet,
  )[0].value;

  if (shouldAddAuxId(product)) {
    auxId =
      product.variantIdentifiedAux.find(({ value }) => value === auxMasterValue)
        ?.masterValue ?? '';
  }

  return auxId ? `${auxValue} - ${auxId}` : auxValue;
}

// Used for birkenstock to add id for narrow or regular
export function shouldAddAuxId(product: IOrderRowProduct) {
  return product.variantIdentifiedAux.length > 0;
}
